import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';

/**
 * DepartmentDropdown - Department selector in the header
 * Opens on hover, styled to match ProjectDropdown
 */
const DepartmentDropdown = ({ selectedDepartment, onSelectDepartment, compact = false, shrink = false, fullWidth = false }) => {
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  // The dropdown panel is rendered through a portal, so it is NOT a DOM descendant of
  // dropdownRef. Without tracking it separately, the click-outside handler treated a
  // tap inside the panel as 'outside' and closed it on touchstart - before the item's
  // click could fire - so on touch devices a selection never registered at all.
  const panelRef = useRef(null);
  const timeoutRef = useRef(null);
  
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const departments = [
    { id: 'projects', label: 'Project Management', icon: '📁' },
    { id: 'estimation', label: 'Estimation', icon: '💰' },
    { id: 'procurement', label: 'Procurement', icon: '🛒' },
    { id: 'resources', label: 'Resource Management', icon: '👥' },
    { id: 'accounting', label: 'Accounting', icon: '⏱️' },
    { id: 'pmo', label: 'PMO', icon: '⚙️' },
    { id: 'hse', label: 'HSE', icon: '🦺' },
    { id: 'quality', label: 'Quality', icon: '✅' },
    { id: 'operations', label: 'Operations', icon: '🏭' },
    { id: 'admin', label: 'Admin', icon: '🔐' },
    { id: 'sales', label: 'Sales', icon: '📊' },
    { id: 'tool-crib', label: 'Tool Crib', icon: '🔧' },
  ];

  const longestLabel = useMemo(() => {
    return departments.reduce((longest, d) => d.label.length > longest.length ? d.label : longest, '');
  }, []);

  // Handle mouse enter - open dropdown
  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsOpen(true);
  };

  // Handle mouse leave - close with delay
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      setSearchTerm('');
    }, 150);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Click outside handler for compact/mobile mode
  useEffect(() => {
    if (!compact || !isOpen) return;
    
    const handleClickOutside = (e) => {
      const inTrigger = dropdownRef.current && dropdownRef.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inTrigger && !inPanel) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [compact, isOpen]);

  const handleSelect = (dept) => {
    onSelectDepartment?.(dept);
    setIsOpen(false);
    setSearchTerm('');
  };

  const filteredDepartments = useMemo(() => {
    if (!searchTerm.trim()) return departments;
    const search = searchTerm.toLowerCase();
    return departments.filter(d => d.label.toLowerCase().includes(search));
  }, [searchTerm]);

  const selectedDept = departments.find(d => d.id === selectedDepartment);
  const selectedLabel = selectedDept?.label || 'Select Department';
  const selectedIcon = selectedDept?.icon || '📁';

  // For mobile touch devices, use click instead of hover
  const handleClick = () => {
    if (compact) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div
      ref={dropdownRef}
      onMouseEnter={!compact ? handleMouseEnter : undefined}
      onMouseLeave={!compact ? handleMouseLeave : undefined}
      style={fullWidth
        ? { position: 'relative', display: 'block', width: '100%', minWidth: 0 }
        : { position: 'relative', display: 'inline-block', minWidth: compact ? 48 : 0, flexShrink: shrink ? 1 : 0, overflow: 'hidden', maxWidth: compact ? '110px' : undefined }}
    >
      {/* Hidden span to measure the longest label for fixed button width */}
      <span
        ref={el => {
          // Pins every department to one width so the button does not jump when the
          // selection changes. Skipped while shrinking: that fixed floor (the width
          // of the longest department name) is one of the reasons the full header
          // could not fit on a laptop.
          if (el && !compact && !shrink) {
            const width = el.offsetWidth;
            if (triggerRef.current && width > 0) {
              triggerRef.current.style.minWidth = `${width + 42}px`;
            }
          } else if (triggerRef.current) {
            triggerRef.current.style.minWidth = '';
          }
        }}
        style={{
          position: 'absolute',
          visibility: 'hidden',
          whiteSpace: 'nowrap',
          fontSize: fullWidth ? '0.85rem' : (compact ? '0.75rem' : '0.9rem'),
          fontWeight: '500',
          letterSpacing: '0.2px',
          pointerEvents: 'none',
        }}
      >
        {longestLabel}
      </span>

      {/* Trigger Button */}
      <button
        ref={triggerRef}
        onClick={handleClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: compact ? '4px' : '6px',
          height: fullWidth ? '40px' : (compact ? '36px' : '44px'),
          width: fullWidth ? '100%' : undefined,
          justifyContent: fullWidth ? 'space-between' : undefined,
          padding: fullWidth ? '0 0.75rem' : (compact ? '0 0.5rem' : '0 1.25rem'),
          background: 'transparent',
          borderRadius: '8px',
          cursor: 'pointer',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          transition: 'all 0.2s ease',
          // 100% of the wrapper, so the button shrinks WITH its flex item instead of
          // spilling over the neighbouring dropdown.
          maxWidth: '100%',
          minWidth: 0,
          overflow: 'hidden',
          fontSize: compact ? '0.75rem' : '0.9rem',
          fontWeight: '500',
          letterSpacing: '0.2px',
          color: 'white',
          boxSizing: 'border-box',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(220, 53, 69, 0.95) 0%, rgba(185, 28, 28, 0.95) 100%)';
          e.currentTarget.style.borderColor = '#dc3545';
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(220, 53, 69, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
        title={selectedLabel}
      >
        {/* Icon for compact mode */}
        {(compact || fullWidth) && <span style={{ fontSize: '14px', flexShrink: 0 }}>{selectedIcon}</span>}
        
        {/* Department name - show abbreviated on compact */}
        <span style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
        }}>
          {compact && !fullWidth ? selectedLabel.substring(0, 4) : selectedLabel}
        </span>
        
        {/* Dropdown arrow */}
        <span style={{
          fontSize: '8px',
          marginLeft: compact ? '2px' : '4px',
          opacity: 0.8,
          transition: 'transform 0.2s ease',
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
        }}>
          ▼
        </span>
      </button>

      {/* Dropdown Panel - Using Portal */}
      {isOpen && createPortal(
        <div
          ref={panelRef}
          onMouseEnter={!compact ? handleMouseEnter : undefined}
          onMouseLeave={!compact ? handleMouseLeave : undefined}
          style={{
            position: 'fixed',
            top: triggerRef.current 
              ? `${triggerRef.current.getBoundingClientRect().bottom + 8}px` 
              : '60px',
            left: compact 
              ? '10px'  // On mobile, anchor to left edge with padding
              : (triggerRef.current 
                ? `${triggerRef.current.getBoundingClientRect().left}px` 
                : '100px'),
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            zIndex: 9999,
            width: compact ? 'calc(100vw - 20px)' : '260px',
            maxWidth: compact ? '280px' : '260px',
            maxHeight: '450px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            animation: 'dropdownSlideIn 0.15s ease-out',
          }}
        >
          {/* Search Header */}
          <div style={{
            padding: '12px',
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb',
          }}>
            <div style={{
              fontSize: '11px',
              color: '#6b7280',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '8px',
            }}>
              Select Department
            </div>
            <input
              type="text"
              placeholder="Search departments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setIsOpen(false);
                  setSearchTerm('');
                } else if (e.key === 'Enter' && filteredDepartments.length === 1) {
                  handleSelect(filteredDepartments[0].id);
                }
              }}
              autoFocus
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.2s ease',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => e.target.style.borderColor = '#dc3545'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
          </div>

          {/* Department List */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            maxHeight: '320px',
          }}>
            {filteredDepartments.length === 0 ? (
              <div style={{
                padding: '24px',
                textAlign: 'center',
                color: '#6b7280',
              }}>
                No departments match your search
              </div>
            ) : filteredDepartments.map((dept) => {
              const isSelected = selectedDepartment === dept.id;
              return (
                <div
                  key={dept.id}
                  onClick={() => handleSelect(dept.id)}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f3f4f6',
                    backgroundColor: isSelected ? '#fef2f2' : 'transparent',
                    transition: 'background-color 0.15s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {/* Department Icon */}
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    backgroundColor: isSelected ? '#dc3545' : '#e5e7eb',
                    color: isSelected ? 'white' : '#374151',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    flexShrink: 0,
                  }}>
                    {dept.icon}
                  </div>
                  
                  {/* Department Name */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: isSelected ? '600' : '500',
                      color: isSelected ? '#dc3545' : '#111827',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {dept.label}
                    </div>
                  </div>
                  
                  {/* Checkmark for selected */}
                  {isSelected && (
                    <div style={{
                      color: '#dc3545',
                      fontSize: '14px',
                      fontWeight: '600',
                    }}>
                      ✓
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Animation Keyframes */}
          <style>{`
            @keyframes dropdownSlideIn {
              from {
                opacity: 0;
                transform: translateY(-8px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
          `}</style>
        </div>,
        document.body
      )}
    </div>
  );
};

export default DepartmentDropdown;
